import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/PlansEnhanced.module.css';
import IndividualPlans from '../components/plans/IndividualPlans';
import OrganizationPlans from '../components/plans/OrganizationPlans';
import FeatureShowcase from '../components/plans/FeatureShowcase';
import { useOrganization } from '../context/OrganizationContext';

const PlansEnhanced = () => {
  const navigate = useNavigate();
  const {
    isOrganizationUser,
    loading,
    userContext,
    currentOrganization,
    isInOrganizationMode,
    hasIndividualSubscription,
    userRole
  } = useOrganization();
  const [activeTab, setActiveTab] = useState('individual');

  // Redirect non-owner organization users to dashboard
  useEffect(() => {
    if (!loading && isInOrganizationMode && userRole && userRole !== 'owner') {
      navigate('/dashboard');
    }
  }, [loading, isInOrganizationMode, userRole, navigate]);

  // Auto-switch to organization tab if user is in organization mode and doesn't have individual subscription
  useEffect(() => {
    if (isInOrganizationMode && !hasIndividualSubscription) {
      setActiveTab('organization');
    }
  }, [isInOrganizationMode, hasIndividualSubscription]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <section
      className={styles['plans-section']}
      aria-labelledby="plans-heading"
    >
      {/* Header */}
      <div className={styles['header']}>
        <h1 id="plans-heading" className={styles['title']}>
          Choose Your Plan
        </h1>
        <p className={styles['subtitle']}>
          Flexible pricing for pilots and organizations
        </p>
      </div>

      {/* Tab Selector */}
      <Box className={styles['tab-container']}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          aria-label="Plan type selection"
          className={styles['tabs']}
          TabIndicatorProps={{
            className: styles['tab-indicator']
          }}
        >
          {/* Show Individual tab if user is in individual mode OR has individual subscription */}
          {(!isInOrganizationMode || hasIndividualSubscription) && (
            <Tab
              value="individual"
              aria-label="Individual plans for solo pilots"
              id="tab-individual"
              aria-controls="tabpanel-individual"
              label={
                <div className={styles['tab-label']}>
                  <PersonIcon
                    className={styles['tab-icon']}
                    aria-hidden="true"
                    fontSize="medium"
                  />
                  <div className={styles['tab-text']}>
                    <div className={styles['tab-title']}>Individual Plans</div>
                    <div className={styles['tab-subtitle']}>For solo pilots</div>
                  </div>
                </div>
              }
              className={styles['tab']}
            />
          )}
          <Tab
            value="organization"
            aria-label="Organization plans for teams and businesses"
            id="tab-organization"
            aria-controls="tabpanel-organization"
            label={
              <div className={styles['tab-label']}>
                <BusinessIcon
                  className={styles['tab-icon']}
                  aria-hidden="true"
                  fontSize="medium"
                />
                <div className={styles['tab-text']}>
                  <div className={styles['tab-title']}>Organization Plans</div>
                  <div className={styles['tab-subtitle']}>For teams &amp; business</div>
                </div>
              </div>
            }
            className={styles['tab']}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box
        role="tabpanel"
        id="tabpanel-individual"
        aria-labelledby="tab-individual"
        hidden={activeTab !== 'individual'}
        className={styles['tab-panel']}
      >
        {activeTab === 'individual' && (
          <IndividualPlans onSwitchToOrg={() => setActiveTab('organization')} />
        )}
      </Box>

      <Box
        role="tabpanel"
        id="tabpanel-organization"
        aria-labelledby="tab-organization"
        hidden={activeTab !== 'organization'}
        className={styles['tab-panel']}
      >
        {activeTab === 'organization' && (
          <OrganizationPlans />
        )}
      </Box>

      {/* Feature Showcase */}
      <FeatureShowcase />
    </section>
  );
};

export default PlansEnhanced;
